import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, Truck } from "lucide-react";
import { useStore } from "@/context/StoreContext";
import { inr } from "@/lib/api";

export default function CartDrawer() {
  const { cart, cartOpen, setCartOpen, setQty } = useStore();
  const navigate = useNavigate();
  const { items, totals } = cart;

  const goCheckout = () => {
    setCartOpen(false);
    navigate("/checkout");
  };

  return (
    <AnimatePresence>
      {cartOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70]" data-testid="cart-drawer">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setCartOpen(false)} />
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ ease: [0.22, 1, 0.36, 1], duration: 0.5 }}
            className="absolute right-0 top-0 flex h-full w-full max-w-md flex-col bg-forest-deep">
            <div className="flex items-center justify-between border-b border-white/10 p-6">
              <div className="flex items-center gap-3">
                <ShoppingBag className="text-gold" size={20} />
                <span className="font-display text-2xl tracking-wide text-cream">YOUR CART</span>
              </div>
              <button onClick={() => setCartOpen(false)} aria-label="Close cart" data-testid="cart-close-btn"
                className="grid h-9 w-9 place-items-center rounded-full hover:bg-white/10"><X className="text-white/80" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {items.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center text-center">
                  <ShoppingBag size={48} className="text-white/20" />
                  <p className="mt-4 font-oswald text-lg text-white/60">Your cart is empty</p>
                  <button onClick={() => { setCartOpen(false); navigate("/shop"); }}
                    className="mt-6 rounded-full gold-gradient px-6 py-2.5 font-oswald text-sm uppercase tracking-wider text-black">
                    Start Shopping
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {items.map(({ product, qty }) => (
                    <div key={product.id} className="flex gap-4 rounded-2xl border border-white/10 bg-white/5 p-3"
                      data-testid={`cart-item-${product.slug}`}>
                      <img src={product.image} alt={product.name} loading="lazy"
                        className="h-20 w-16 rounded-xl object-cover" />
                      <div className="flex flex-1 flex-col">
                        <span className="font-oswald text-sm text-cream leading-tight">{product.name}</span>
                        <span className="text-xs" style={{ color: product.accent }}>{product.flavour} · {product.weight}</span>
                        <div className="mt-auto flex items-center justify-between">
                          <div className="flex items-center gap-2 rounded-full border border-white/15">
                            <button onClick={() => setQty(product.id, qty - 1)} aria-label="Decrease"
                              data-testid={`cart-dec-${product.slug}`}
                              className="grid h-7 w-7 place-items-center text-white/70 hover:text-gold"><Minus size={13} /></button>
                            <span className="w-5 text-center font-oswald text-sm text-cream">{qty}</span>
                            <button onClick={() => setQty(product.id, qty + 1)} aria-label="Increase"
                              data-testid={`cart-inc-${product.slug}`}
                              className="grid h-7 w-7 place-items-center text-white/70 hover:text-gold"><Plus size={13} /></button>
                          </div>
                          <span className="font-oswald text-cream">{inr(product.price * qty)}</span>
                        </div>
                      </div>
                      <button onClick={() => setQty(product.id, 0)} aria-label="Remove"
                        data-testid={`cart-remove-${product.slug}`}
                        className="self-start text-white/40 hover:text-red-400"><Trash2 size={16} /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {items.length > 0 && (
              <div className="border-t border-white/10 p-6">
                <div className="space-y-2 text-sm">
                  <Row label="Subtotal" value={inr(totals.subtotal)} />
                  <div className="flex items-center justify-between text-white/60">
                    <span className="flex items-center gap-1.5"><Truck size={14} /> Delivery</span>
                    <span>{totals.delivery === 0 ? "FREE" : inr(totals.delivery)}</span>
                  </div>
                  {totals.delivery > 0 && (
                    <p className="text-[11px] text-gold/70">Add {inr(499 - totals.subtotal)} more for free delivery</p>
                  )}
                </div>
                <div className="my-4 flex items-center justify-between border-t border-white/10 pt-4">
                  <span className="font-oswald text-lg text-cream">TOTAL</span>
                  <span className="font-oswald text-2xl text-gold">{inr(totals.total)}</span>
                </div>
                <button onClick={goCheckout} data-testid="checkout-btn"
                  className="w-full rounded-full gold-gradient py-3.5 font-oswald text-sm uppercase tracking-[0.15em] text-black transition-transform hover:scale-[1.02]">
                  Checkout
                </button>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between text-white/60"><span>{label}</span><span className="text-cream">{value}</span></div>
);
